// One line: a number, a package, an owner, a place — and everything that has to be true
// for it to ring.
//
// WHY THIS PAGE EXISTS. A line was the thing we sell and the only thing without a page.
// Its settings were scattered across the surfaces that happened to own the underlying
// record: caller ID inside the DID page's customer card, voicemail inside a collapsed
// "account-wide" disclosure on the subscriber page, the handset port in a modal, 911 on
// the location page, and provisioning behind a customer-wide button that swept every
// number at once. An operator asked to fix ONE line had to know which four screens hold
// the pieces, and the one action available worked on all of them.
//
// So: the line is the object, this is its page, and the two verbs at the bottom act on
// this line alone. Everything here is either the line's own state or a link to the object
// that genuinely owns it (the customer, the address, the handset) — the page does not
// re-implement those, it just stops hiding them.

import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import useHashTab from '../hooks/useHashTab.js';
import useBackTo from '../hooks/useBackTo.js';
import MoveLineModal from '../components/lines/MoveLineModal.jsx';
import MigrateExtensionModal from '../components/subscribers/MigrateExtensionModal.jsx';

// Module scope: useHashTab memoises on these. Lowercase ids so the URL is stable and
// readable; the labels stay title-case on screen.
const LINE_TABS = ['overview', 'routing', 'voicemail', 'messaging', 'diagnostics'];
const LINE_TAB_LABELS = {
  overview: 'Overview', routing: 'Routing', voicemail: 'Voicemail', messaging: 'Messaging',
  // Kevin, 2026-08-22: "need to be able to view live calls and call tracing from the line detail
  // page." The rail's card is 320px — fine for "are the phones up", useless for a SIP message.
  diagnostics: 'Diagnostics',
};
// `features` was a tab whose only real content was voicemail; the rest of it was two
// sentences pointing somewhere else. Kevin, 2026-08-13: "the voicemail is per line... maybe
// there should be a dedicated tab for each line for voicemail and another for messaging."
// Kept as an alias so a bookmark or a link written against the old page still lands on the
// content it was pointing at rather than silently snapping to Overview.
const LINE_TAB_ALIASES = { features: 'voicemail', sms: 'messaging' };
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { fmtDateTime } from '../lib/time.js';
import { useAuth } from '../hooks/useAuth.js';
import { canOperate, canAdmin } from '../lib/roles.js';
import LoadingSpinner from '../components/shared/LoadingSpinner.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';

// A per-feature chip for the page header: on, off, not-in-package, or unknown.
//
// Four states because three of them are routinely confused on this product: a feature the customer
// did not buy is not "off", and a host we could not reach has not told us anything. `on === null`
// means unknown and is drawn as such — rendering it as off is the lie that made a badge read "On"
// for a mailbox the PBX did not have (2026-08-17).
function FeatureBadge({ label, inPackage, on, unknownTitle }) {
  if (!inPackage) {
    return (
      <span title={`Not included in this line's package`}
            className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-400">
        {label} n/a
      </span>
    );
  }
  if (on === null || on === undefined) {
    return (
      <span title={unknownTitle || 'Could not be confirmed'}
            className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-500">
        {label} ?
      </span>
    );
  }
  return (
    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
      on ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
    }`}>
      {label} {on ? 'on' : 'off'}
    </span>
  );
}
import ConfirmDeleteDialog from '../components/shared/ConfirmDeleteDialog.jsx';
import PendingChanges from '../components/lines/PendingChanges.jsx';
import CallerIdCard from '../components/lines/CallerIdCard.jsx';
import LineRunDialog from '../components/lines/LineRunDialog.jsx';
import SetDevicePortDialog from '../components/lines/SetDevicePortDialog.jsx';
import LineDiagnostics from '../components/lines/LineDiagnostics.jsx';
import LineLiveCalls from '../components/lines/LineLiveCalls.jsx';
import RoutingTab from '../components/lines/RoutingTab.jsx';
import LineVoicemail from '../components/lines/LineVoicemail.jsx';
import LineMessaging from '../components/lines/LineMessaging.jsx';
import LineCallPath from '../components/lines/LineCallPath.jsx';
import LinePortIn from '../components/lines/LinePortIn.jsx';
import LineSettingsModal from '../components/lines/LineSettingsModal.jsx';
import GearButton from '../components/shared/GearButton.jsx';

function Card({ title, hint, children, action }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5">
      {/* A card can be untitled — the Overview card is, since the page header already says
          which line this is and "This line" said nothing a second time (Kevin, 2026-08-11).
          The header row still renders for the gear alone. */}
      {(title || hint || action) && (
        <div className="flex items-start justify-between gap-4">
          <div>
            {title && <h2 className="text-sm font-semibold text-gray-900">{title}</h2>}
            {hint && <p className="mt-1 text-sm text-gray-600">{hint}</p>}
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function Field({ label, value, mono = false }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</dt>
      <dd className={`mt-0.5 text-sm text-gray-900 ${mono ? 'font-mono' : ''}`}>
        {value ?? <span className="text-gray-300">—</span>}
      </dd>
    </div>
  );
}

/*
 * THERE IS NO LEAVE-THE-PAGE GUARD, AND THAT IS DELIBERATE.
 *
 * A `beforeunload` handler used to fire whenever the line had buildable pending changes. It
 * made the browser say "Changes you made may not be saved" — which on this page is simply
 * untrue. Verso's contract is save-then-apply: everything on this page is already saved in
 * Verso the moment it is entered, and "pending" means the PBX, the carrier or GDMS has not
 * been told yet. Nothing is lost by leaving.
 *
 * So it fired on EVERY unbuilt line, every reload — and a reload is exactly what an operator
 * does to see whether a write landed (Kevin hit it twice on 2026-08-13 walking line 136).
 * A dialog that is always on, and wrong about what it is protecting, teaches people to click
 * through dialogs, which is the habit that matters when a real one appears.
 *
 * What is unapplied is reported by the pending card at the top of the page, permanently and
 * in words. That was already the only thing covering in-app navigation, because React
 * Router's own navigation never fired `beforeunload` anyway.
 */

export default function LineDetail() {
  const { id } = useParams();
  // ROLE, not the user object. `canOperate`/`canAdmin` index a level table by role
  // string, so passing `user` yields undefined → 0 → false — which hid every control on
  // this page from everyone, including org_admin, and read as "the page has no actions".
  // That is what Kevin was looking at on 2026-08-04.
  const { role } = useAuth();
  const canOp = canOperate(role);
  const canAdm = canAdmin(role);
  const qc = useQueryClient();

  const [run, setRun] = useState(null);          // 'provision' | 'teardown'
  // The EXTENSION whose device is being set — not a boolean, because the binding is per
  // extension and the dialog has to know which one.
  const [settingPort, setSettingPort] = useState(null);
  // In the URL hash, so a refresh stays on Routing or Features instead of snapping back to
  // Overview — which on a page you refresh to check whether a write landed is the worst
  // possible moment to lose your place (Kevin, 2026-08-13). See hooks/useHashTab.js.
  const [tab, setTab] = useHashTab('overview', { valid: LINE_TABS, aliases: LINE_TAB_ALIASES });
  // The page is INFORMATION plus one verb. Settings are edited in a modal behind the gear
  // on the info card, and saving there is Verso-only; Apply is the only thing that reaches
  // the PBX, the handset or the carrier (Kevin, 2026-08-04). Two save-shaped buttons on one
  // screen — "Save" and "Save and Apply" — could not be told apart, which is what this
  // replaced.
  // null = closed; an array = the fields the sheet is scoped to; [] = all of them.
  // ONE gear for this card, so there is one `open` and nothing to scope it to. There were
  // three — the card's, the caller ID's, the 911 address's — and a `settingsOnly` state that
  // told the sheet which field to show (Kevin, 2026-08-19: "there should just be one in the
  // top right of the card").
  const [settingsOpen, setSettingsOpen] = useState(false);
  // A teardown that is really the first half of a Remove. The line is only deleted if the
  // run actually landed — see onDone below.
  const [removeAfterTeardown, setRemoveAfterTeardown] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Moving the line to another customer's account (ticket #23). Its own modal, because the plan is
  // worth reading before pressing anything — see components/lines/MoveLineModal.jsx.
  const [moving, setMoving] = useState(false);
  // Moving between PBX HOSTS, as distinct from moving between customer accounts above.
  const [migrating, setMigrating] = useState(false);
  const navigate = useNavigate();

  const lineQ = useQuery({
    queryKey: ['line', String(id)],
    queryFn: () => api.get(`/lines/${id}`),
  });
  const line = lineQ.data;

  // A CUTOVER THAT COMMITTED BUT COULD NOT TIDY UP AFTER ITSELF.
  //
  // 2026-09-09: +17208302800 migrated to pbx-04 and committed, and the teardown left the extension
  // and a route on pbx-02. Nothing in the product could clear them, and the line's host record
  // could not be corrected either — the correction route rightly refuses while the old host still
  // serves the line. So it needed a hand-made API call, which is what this exists to stop.
  //
  // Only asked for when there IS a migration to ask about, and only shown when its own teardown
  // step warned. A committed migration that tidied up correctly shows nothing.
  const migQ = useQuery({
    // ITS OWN KEY, not the modal's. `MigrateExtensionModal` also watches `['migrate-status', id]`
    // — with a different queryFn, its own `enabled` and a poll interval — and two observers on one
    // key with different options is a collision waiting to happen. It was: after this query landed,
    // the migrate modal stopped opening at all while every other modal on the page still worked.
    // These are different questions anyway. This one asks "did a past cutover leave a mess".
    queryKey: ['migrate-cleanup-state', line?.sipAccountId],
    queryFn: () => api.get(`/sip-accounts/${line.sipAccountId}/migrate/status`),
    enabled: Boolean(line?.sipAccountId),
    retry: false,
  });
  const teardownStep = (migQ.data?.steps || []).find((x) => x.id === 'teardown-old');
  const needsCleanup = migQ.data?.status === 'committed' && teardownStep?.status === 'warning';
  const cleanup = useMutation({
    mutationFn: () => api.post(`/sip-accounts/${line.sipAccountId}/migrate/teardown`, {}, undefined, { timeoutMs: 60_000 }),
    onSuccess: () => { migQ.refetch?.(); lineQ.refetch?.(); },
  });
  // A HOST THAT CANNOT BE ASKED, as opposed to one that refused. aria-cloud-pbx mounts no delete
  // for an extension or an inbound route, so every key comes back 404 and retrying is guaranteed to
  // fail — offering the button would be offering a lie. The state is still worth showing, because
  // the leftovers are real; what changes is that it stops being an action.
  //
  // Declared AFTER the mutation it reads: hoisting it above `const cleanup` is a TDZ crash, which
  // is how this page went white once already.
  const cleanupImpossible = needsCleanup
    && (teardownStep?.code === 'source_cannot_delete'
      || cleanup.error?.body?.code === 'source_cannot_delete');

  // The arrow returns to the screen the operator left — the subscriber's Lines tab when
  // they came from there. Falls back to the subscriber's page when nothing said.
  const back = useBackTo(line?.subscriberName || 'Subscribers',
    line?.subscriberId ? `/subscribers/${line.subscriberId}` : '/subscribers');


  // Live mailbox state is read by the Voicemail tab itself now, per extension — it used to
  // be read here for the line's OWN account alone, which on a line ringing five phones
  // reported the mailbox of the one nobody dials.

  const del = useMutation({
    mutationFn: () => api.delete(`/lines/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['subscriber'] });
      qc.invalidateQueries({ queryKey: ['dids'] });
      navigate(line?.subscriberId ? `/subscribers/${line.subscriberId}` : '/subscribers');
    },
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['line', String(id)] });
    // The subscriber's lines table carries the same pending summary, and the DIDs list
    // shows the number's caller ID.
    if (line?.subscriberId) {
      qc.invalidateQueries({ queryKey: ['subscriber', String(line.subscriberId)] });
    }
    qc.invalidateQueries({ queryKey: ['dids'] });
    qc.invalidateQueries({ queryKey: ['voicemail-status'] });
  };

  if (lineQ.isLoading) return <LoadingSpinner />;
  if (lineQ.error) {
    return (
      <p className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
        {lineQ.error.body?.error || lineQ.error.message}
      </p>
    );
  }
  if (!line) return <p className="text-sm text-gray-500">Line not found.</p>;

  const pending = line.pending || { count: 0, items: [] };
  // Is there anything for Apply to do? Buildable pending work, or a line that has never been
  // built. Blocked items (an unregistered 911 site) are NOT outstanding-for-Apply: the
  // planner refuses them, so offering the button would advertise work that cannot land.
  const outstanding = (pending.buildable ?? 0) > 0 || !line.provisioned;

  return (
    <div className="space-y-5">
      {/* Leaving with work that Verso holds and the systems carrying the calls do not is the
          case worth interrupting for — a save records, and only Apply pushes, so an operator
          who saves and walks away has changed nothing that rings (Kevin, 2026-08-11). */}

      {/* ---- Identity ---- */}
      <div>
        {/* Back to where they came from, and in the word the rest of the app uses. "Customers"
            appears nowhere in the nav — it says Subscribers — and an operator arriving from a
            subscriber wants that subscriber, not the list. */}
        <Link to={back.href} className="text-xs text-gray-500 hover:text-gray-700">
          ← {back.label}
        </Link>
        <div className="mt-1 flex flex-wrap items-center gap-3">
          <h1 className="font-mono text-2xl font-bold text-gray-900">
            {line.number || <span className="text-gray-400">No number</span>}
          </h1>
          {/* The line's own name, beside the number rather than in a card of its own — it
              is identity, and a card repeating it is how this page grew two of everything. */}
          {line.label && (
            <span className="text-lg font-medium text-gray-700">{line.label}</span>
          )}

          <StatusBadge status={line.status} />
          {/* Sold vs built, which is the distinction the whole page turns on. Only the
              executor writes provisioned_at, and only after a run whose PBX steps all
              landed — so this is a fact, not an inference from an empty column. */}
          {line.provisioned ? (
            // "built", not "on PBX-02": the host is named once, in Routing. This chip is
            // here to say whether a PBX serves the line at all, which is the distinction the
            // page turns on.
            <span className="rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
              built
            </span>
          ) : (
            <span
              title="Sold, but no PBX serves it yet"
              className="rounded-full bg-gray-200 px-2 py-0.5 text-xs font-medium text-gray-700"
            >
              not built
            </span>
          )}

          {/* VOICEMAIL AND MESSAGING, BESIDE THE NUMBER (2026-08-19).
              Kevin: "add badges for voicemail and messaging next to the line number/did" — asked for
              in the same breath as removing the all-clear summary card, and it is the same idea: the
              at-a-glance answer belongs where the identity is, not in a card that repeats itself
              lower down.
              These read the LIVE state, the same fields their tabs' switches read: `mailboxEnabled`
              is the host's answer about the mailbox, tri-state, and null must not render as off.
              A feature the package excludes says so rather than looking merely switched off. */}
          <FeatureBadge
            label="Voicemail"
            inPackage={line.voicemailInPackage !== false}
            on={line.voicemailEnabled === false ? false : line.mailboxEnabled}
          />
          {/* MESSAGING IS NOT AN UNKNOWN. Kevin, 2026-08-20: "what is the 'Messaging ?' badge
              supposed to mean? Why the '?'"

              It read `smsEnabled === 0 ? false : null`, so anything other than an explicit 0 —
              which is to say almost every line in the fleet, because the column is NULL until
              somebody switches messaging on — drew a "?" meaning "could not be confirmed". Nothing
              had failed to be confirmed. Six inches below it the diagnostics card said, from the
              same field, "never switched on", and under that "At the carrier: not enabled".

              WHETHER A LINE CAN TEXT IS VERSO'S OWN FACT, not the carrier's. The black-hole gate in
              lib/smsPipeline.js is `sms_enabled !== 1` → discard, so NULL and 0 both mean no text
              reaches anybody; they differ only in whether a human ever decided it. That is a
              distinction for the diagnostics card, which draws it, and not for a chip whose whole
              job is to answer "can this line text?" — where the answer is no.

              `?` still exists on this badge and still means what it says. Voicemail earns it: the
              mailbox lives on the PBX, so an unreachable host genuinely leaves us not knowing. */}
          <FeatureBadge
            label="Messaging"
            inPackage={line.smsInPackage !== false}
            on={line.smsEnabled === 1}
          />
        </div>
        {/* Joined, not concatenated with separators: hard-coding " · " between fields put a
            bare "Business Standard · ·" on line 134, whose subscriber link renders as its
            own element. Build the list, drop the empties, then join. */}
        <p className="mt-1 flex flex-wrap items-center gap-x-2 text-sm text-gray-600">
          <span>
            {line.packageName || <span className="text-amber-700">No package</span>}
            {/* The other half of the pair the carrier insists must agree. Shown next to the
                address type so "residential package, business address" is visible rather
                than inferred. */}
            {line.packageType && (
              <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                {line.packageType}
              </span>
            )}
          </span>
          {/* The subscriber is already the back link above, to the same place. Two links to
              one destination, one line apart, is the duplication this page keeps regrowing. */}
          {/* The address is NOT repeated here. It lives in the 911 card with its carrier
              registration, which is what anybody actually needs it for. Kevin, 2026-08-10:
              "information is repeated or duplicated." */}
        </p>
      </div>

      {/* AT THE TOP, AND ONLY WHEN IT HAS SOMETHING TO SAY (Kevin, 2026-08-11). The all-clear
          state is the common one, and a green card repeating "nothing is wrong" on every
          visit is how a card stops being read — so a clean line shows nothing here at all and
          the eye goes straight to the tabs. Notes count as something to say: they exist
          because "nothing outstanding" was true and still misleading. */}
      {(pending?.items?.length || pending?.unknown?.length || pending?.notes?.length) > 0 && (
        <PendingChanges pending={pending} />
      )}

      {/* Two columns. The tabs answer "what is this line and what does it do"; the rail
          answers "is it working and does it owe anything", which is true on every tab —
          Kevin, 2026-08-11. It replaces the message card that used to sit above the tabs:
          the state it reported is still reported, it just no longer pushes the page down
          on the way to the thing you came for. Stacks on narrow screens. */}
      <div className="flex flex-col gap-5 lg:flex-row lg:items-start">
      <div className="min-w-0 flex-1 space-y-5">

      {/* Three tabs, per Kevin 2026-08-10: what the line IS, how a call reaches it, and
          what it can do. The page had grown a single column of eight cards. */}
      <div className="flex flex-wrap gap-1 border-b border-gray-200" role="tablist">
        {LINE_TABS.map((t) => (
          <button
            key={t}
            role="tab"
            aria-selected={tab === t}
            onClick={() => setTab(t)}
            className={`-mb-px rounded-t-md border-b-2 px-3 py-2 text-sm ${
              tab === t
                ? 'border-blue-600 font-semibold text-blue-700'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {LINE_TAB_LABELS[t]}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        // ONE card. Kevin: "Overview only needs a single card that combines everything."
        // Three cards for six facts was the repetition this page keeps growing back.
        <Card
            action={canAdm && (
              // The card's own gear, not a page-level button: it edits THIS card's fields —
              // all of them, which is why it is the only one on the card.
              <GearButton onClick={() => setSettingsOpen(true)} />
            )}
          >
          {/* EVERYTHING THIS LINE *IS*, IN ONE CARD. Kevin, 2026-08-13: "merge the caller id
              and e911 into the top overview card along with status, package, etc."

              Caller ID and the 911 address had cards of their own directly below this one,
              which made three cards for five facts — and the two below were the ragged ones,
              each with a line or two of carrier state hanging off it. They are still fully
              editable here: each keeps its own gear, its own sheet and every word of its
              carrier state. Only the borders went.

              THE HOST IS NOT A FIELD ANY MORE either. It was, until the call-path diagram
              landed on this tab and drew the host as the node it is, with what it does with
              the number underneath. A label reading "PBX host: PBX-02" three inches above a
              box reading "PBX-02 · inbound route installed" is the duplication this page
              keeps regrowing (Kevin, 2026-08-10: "information is repeated or duplicated").
              The gear still edits it; the diagram still names it. */}
          <dl className="mt-3 grid gap-x-6 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
            {/* A PORT IN FLIGHT OUTRANKS "active". Kevin, 2026-08-20: "change the Status in the
                overview card to 'Port Inprogress'". The line IS active — the header badge beside
                the number still says so — so nothing is lost by this field carrying the fact
                somebody actually needs, which is that the number is still moving. */}
            <Field label="Status" value={line.portInProgress ? 'Port in progress' : line.status} />
            <Field
              label="Package"
              value={line.packageName ? (
                <>
                  {line.packageName}
                  {line.packageType && (
                    <span className="ml-1.5 rounded-full bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-600">
                      {line.packageType}
                    </span>
                  )}
                </>
              ) : null}
            />
            <Field
              label="Name"
              value={line.label || (
                <span className="text-gray-500">
                  {line.subscriberName || 'the customer’s name'}
                  <span className="block text-xs">not set — falls back to this</span>
                </span>
              )}
            />

            {/* Editable through the card's gear, which is the only way to change it — folding
                it in here once before removed that path entirely. It also does not hide when
                the caller ID matches the customer's name: a field that appears and disappears
                depending on whether two strings happen to agree left lines with no way to edit
                theirs. */}
            {line.number && (
              <CallerIdCard
                variant="field"
                e164={line.number}
                cnam={line.cnam}
                cnamType={line.locationType || line.packageType || line.subscriberType || null}
                subscriberCallerIdId={line.subscriberCallerIdId}
                callerIdId={line.commioCallerIdId}
                subscriberId={line.subscriberId}
                subscriberName={line.subscriberName}
                /* Displayed here, edited in the card's one sheet — which is where the line's
                   NAME is edited too, and the point is that an operator sees the two fields
                   together and can tell them apart. The card keeps its own gear on the DID
                   page, where caller ID is the whole card. */
                canEdit={false}
                onSaved={refresh}
              />
            )}

            {/* The one field on this page where being wrong sends an ambulance to the wrong
                house — so it keeps every word it had, including whether the carrier has it.
                It had a gear of its own, opening the settings sheet scoped to this one field.
                The sheet still holds it, beside the package its type has to agree with; the
                card's gear is the way in. */}
            <div className="sm:col-span-2 lg:col-span-1">
              <div className="flex items-start justify-between gap-2">
                <dt className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  911 address
                </dt>
              </div>
              <dd className="mt-0.5">
                {line.locationId ? (
                  <div className="text-sm">
                    <Link to={`/locations/${line.locationId}`} className="text-blue-600 hover:underline">
                      {line.locationLabel || line.locationAddress}
                    </Link>
                    {line.locationType && (
                      <span className="ml-1.5 rounded-full bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-700">
                        {line.locationType}
                      </span>
                    )}
                    {/* Only when it says something the label does not. Most locations are
                        labelled with their own street address, and printing it twice is the
                        duplication this page keeps regrowing. */}
                    {line.locationAddress && line.locationAddress !== line.locationLabel && (
                      <p className="mt-0.5 text-xs text-gray-500">{line.locationAddress}</p>
                    )}
                    {/* Registered at the carrier, or not — the field that decides where an
                        ambulance goes should not be silent about it. */}
                    <p className={`mt-1 text-xs ${line.locationCommioId ? 'text-gray-500' : 'text-amber-800'}`}>
                      {line.locationCommioId
                        ? `Registered with the carrier as site #${line.locationCommioId}.`
                        : 'Not registered with the carrier — a 911 call from this line has no address behind it.'}
                    </p>
                  </div>
                ) : (
                  <p className="text-sm text-amber-800">
                    None set. A line cannot be built without one.
                  </p>
                )}
              </dd>
            </div>
          </dl>
        </Card>
      )}

      {/* A LINE WITH NO NUMBER IS EITHER WAITING OR UNFINISHED, and only one of those is
          somebody's fault. Directly under the card whose headline reads "No number", because that
          is where an operator asks why not and when — and because the alternative was an operator
          reading `no_number` as a dropped sale.

          MOUNTED FOR EVERY LINE, not only numberless ones. Once a stand-in number is standing in
          (Kevin, 2026-08-20) the line HAS a number, and gating on that made the panel disappear at
          the exact moment the port needed watching, the forward needed confirming and the borrowed
          number needed handing back. It renders nothing unless there is a port to talk about. */}
      <LinePortIn line={line} canWrite={canAdm} onChanged={refresh} />

      {/* THE PATH, DRAWN — carrier, host, extensions, phones (Kevin, 2026-08-13). It sat on
          Routing as three labelled rows, which is a shape that cannot show a branch: a number
          ringing five phones rendered "answers on —" and "no phone" because a list has one
          slot per row. It is on OVERVIEW because "what happens when someone dials this
          number" is the first thing an operator wants and is not a routing edit — Routing
          keeps the controls that CHANGE the path, this shows the path. */}
      {tab === 'overview' && <LineCallPath line={line} />}

      {/* ONE QUESTION FIRST. Until an operator has said how calls reach this line, nothing
          else on the tab can be set up — the extensions below are members of a route that
          does not exist yet (Kevin, 2026-08-11). So the chooser is the whole tab until it is
          answered. The carrier route and the PBX interface are NOT here: they are host-level
          facts configured under the PBX host, and the one per-line fact — which host — is on
          Overview. */}
      {/* What the PBX ACTUALLY does with this number, above the chooser — because a
          line pointed at a queue or an IVR would otherwise show only the two options
          Verso can build, implying those are the choices. Renders nothing when Verso
          built the route (#229, read-only). */}
      {/* ONE COMPONENT owns the tab's shape now, because the shape is the thing that was getting
          away from us. See RoutingTab.jsx for the principle: one destination, then only the cards
          that describe what is inside THAT destination. Stacking every card unconditionally is how
          a tab becomes a pile, and it is how the auto attendant ended up inside the drift card. */}
      {tab === 'routing' && (
        <RoutingTab
          line={line}
          canEdit={canAdm}
          onSaved={refresh}
          onChooseDevice={(e) => setSettingPort(e)}
        />
      )}

      {tab === 'voicemail' && (
        <LineVoicemail line={line} canWrite={canAdm} />
      )}

      {tab === 'messaging' && (
        <LineMessaging line={line} canWrite={canAdm} />
      )}

      {tab === 'diagnostics' && (
        <LineLiveCalls line={line} canWrite={canAdm} />
      )}

      {/* WHAT IS NOT MODELLED, said on the tab where it would live rather than in a
          drawer of its own. Call forwarding IS built — `lib/forwarding.js` reads and
          writes it against FreePBX — but only the subscriber portal is wired to it, so
          there is nothing here to show yet and a card pretending otherwise would be
          worse than this sentence. Forwarding belongs on THIS tab when it is wired:
          "where does a call go when nobody answers" is the same question the route type
          and the ring group answer. Call waiting is not modelled at all. */}
      {tab === 'routing' && (line.routeChosen || line.multiExtension === false) && (
        <p className="text-xs text-gray-500">
          Call forwarding and call waiting are set per extension on the PBX. Subscribers can
          set forwarding in their own portal; it is not manageable from here yet.
        </p>
      )}

      </div>

      {/* The rail. Sticky, so it stays in view on a long tab, and it holds the two things
          that is never about which tab you are on: whether the phones are actually up. What
          is outstanding went back to the top, where a page-wide alert belongs. */}
      <aside className="w-full space-y-4 lg:sticky lg:top-4 lg:w-80 lg:flex-shrink-0">
        <LineDiagnostics line={line} />
      </aside>
      </div>

      {/* ---- One verb. ----
          Apply is live only when there is outstanding work to push: everything else on this
          page either reads, or saves to Verso through the gear. A button that is always
          clickable teaches nothing about whether the line needs anything. */}
      {canOp && (
        <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-4">
          {canAdm && (
            <>
              {/* THE HALF-FINISHED CUTOVER, named where the operator can act on it. Amber rather
                  than red: the line is working — calls arrive at the new host and the phone is
                  registered there — but the old host still holds an extension and a route, which
                  is why its own host record still reads wrong. */}
              {needsCleanup && (
                <div className="mb-3 w-full rounded-md border border-amber-300 bg-amber-50 px-3 py-2">
                  <p className="text-sm font-medium text-amber-900">
                    {migQ.data?.oldAgentName || 'The old host'} still has this line’s extension
                    and route.
                  </p>
                  <p className="mt-0.5 text-xs text-amber-800">
                    {cleanupImpossible
                      ? `${migQ.data?.oldAgentName || 'That host'} can’t delete extensions or routes yet, `
                        + 'so they stay there. Nothing reaches them and the move is complete.'
                      : 'Calls are reaching the new host. Until the old one is cleared, this page can '
                        + 'still show the wrong PBX for this line.'}
                  </p>
                  {/* NO BUTTON WHEN THERE IS NOTHING TO PRESS IT AT. Every key we can send that
                      host comes back 404, so "Finish cleanup" could only ever fail — and it did,
                      twice, printing a raw 404 under a cutover that had actually gone perfectly. */}
                  {!cleanupImpossible && (
                    <button
                      type="button"
                      onClick={() => cleanup.mutate()}
                      disabled={cleanup.isPending}
                      className="mt-2 cursor-pointer rounded-md bg-amber-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-800 disabled:cursor-wait disabled:opacity-70"
                    >
                      {cleanup.isPending ? 'Clearing the old host…' : 'Finish cleanup'}
                    </button>
                  )}
                  {cleanup.isError && !cleanupImpossible && (
                    <p className="mt-2 text-sm text-red-700">
                      {cleanup.error.body?.message || cleanup.error.body?.error || cleanup.error.message}
                    </p>
                  )}
                  {cleanup.data?.ok && (
                    <p className="mt-2 text-sm text-green-800">
                      Cleared from {cleanup.data.oldAgentName}. Nothing is left on it for this line.
                    </p>
                  )}
                </div>
              )}
              {/* THE TOOLTIP HANGS ON THE SPAN, NOT THE BUTTON. A disabled control takes no
                  pointer events, so its own `title` never appears — which is exactly when the
                  operator needs it: the button is grey and they want to know why (Kevin,
                  2026-08-11). The wrapper is hoverable and the button inside is not. */}
              <span
                title={outstanding
                  ? 'Push this line to the PBX, the phone and the carrier'
                  // "Nothing to push" and "nothing is outstanding" are different, and saying
                  // the second when the first is true contradicted the banner one screen
                  // above: "1 pending change" over "everything has already reached them"
                  // (Kevin's review, line 122). Blocked work is still work.
                  : (pending?.blocked ?? 0) > 0
                    ? 'Nothing a build can do — the pending change in the rail needs a decision or the agent'
                    : 'Nothing to push — everything Verso holds has already reached them'}
                className="inline-block"
              >
                <button
                  type="button"
                  disabled={!outstanding}
                  onClick={() => setRun('provision')}
                  className="rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:pointer-events-none disabled:opacity-50"
                >
                  Save and Apply
                </button>
              </span>
              {/* RE-APPLY WHEN NOTHING IS OUTSTANDING, because "nothing to push" is a claim about
                  VERSO and not about the phone.
                  2026-09-09: GDMS had lost a SIP account's device binding, the DECT base was
                  unsynchronised and three lines were down. Verso held no pending change, so the
                  button above was correctly grey and there was NO WAY to re-push from the UI at
                  all — the repair had to be an API call, which is exactly what Kevin has said
                  should never be necessary.
                  Deliberately secondary and deliberately not the same control: the primary button
                  means "you have changes", and blurring that was the alternative. */}
              {!outstanding && (
                <button
                  type="button"
                  onClick={() => setRun('provision')}
                  title="Push this line to the PBX, the phone and the carrier again — for when one of them has drifted from what Verso holds"
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Re-apply
                </button>
              )}
              {/* NO "Move extensions here" BUTTON. Folding a customer's extension-only
                  lines into the line that owns the number is onboarding work, done once
                  per account — Kevin, 2026-08-17: "something that we will only do as part
                  of the original onboarding of each account and is a backend feature that
                  doesn't need to be brought out". `POST /lines/:id/absorb` remains for
                  that; it just does not belong in the day-to-day operator surface. */}
              {/* MOVING IS NOT REMOVING, and putting them side by side is the point: ticket #23 was a
                  line on the wrong account, and without this the only tools were "delete it and sell
                  it again" or a hand-edited row. See components/lines/MoveLineModal.jsx. */}
              <button
                type="button"
                onClick={() => setMoving(true)}
                title="Move this line, its number and its extension to a different customer's account"
                className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Move to another account
              </button>
              {/* TWO DIFFERENT MOVES, SIDE BY SIDE ON PURPOSE. The one above changes WHO owns the
                  line; this changes WHICH PBX answers for it. They were easy to confuse while only
                  one existed, and the wrong one is expensive in both directions.

                  This is the entry point components/subscribers/MigrateExtensionModal.jsx was parked
                  waiting for (#12 → task #230): driven from the LINE, so the number and its
                  extension cross together. Until now the migration saga was live and tested with
                  nothing in the dashboard that could reach it — an operator could not move a line
                  between hosts at all. */}
              {line.sipAccountId && (
                <button
                  type="button"
                  onClick={() => setMigrating(true)}
                  title="Move this line's number and extension to a different PBX host"
                  className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Move to another PBX
                </button>
              )}
              <button
                type="button"
                disabled={del.isPending}
                onClick={() => { del.reset(); setConfirmDelete(true); }}
                title={line.provisioned
                  ? 'Take this line off the PBX, the phone and the carrier, then remove it'
                  : 'Remove this line and return its number to inventory'}
                className="rounded-md border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
              >
                Remove
              </button>
            </>
          )}
          {del.error && (
            <span className="text-xs text-red-700">{del.error.body?.error || del.error.message}</span>
          )}
          <span className="ml-auto text-xs text-gray-400">
            {/* parseTs, not `new Date` — see lib/time.js. This line read six hours into the
                future for a run that had just finished. */}
            {line.provisionedAt ? `Built ${fmtDateTime(line.provisionedAt)}` : 'Never built'}
          </span>
        </div>
      )}

      {run && (
        <LineRunDialog
          line={line}
          mode={run}
          onClose={() => { setRun(null); setRemoveAfterTeardown(false); }}
          onDone={(res) => {
            refresh();
            // Only delete when the teardown actually landed. A partial teardown that got
            // deleted anyway would leave an extension and a route on the PBX with nothing
            // in Verso to explain them.
            if (removeAfterTeardown && res?.ok) del.mutate();
            setRemoveAfterTeardown(false);
          }}
        />
      )}

      {migrating && (
        <MigrateExtensionModal
          // THE HOST IT IS ON, so the target list can leave it out. The modal used to learn this
          // from the plan — which only loads once a target is chosen, so the dropdown offered the
          // current host and answered "already on that PBX host" after the operator picked it.
          // `extensionAgentId` rather than `pbxAgentId`: the extension's own host is what the saga
          // migrates FROM, and when the two have drifted apart moving to the line's recorded host
          // is a legitimate correction rather than a no-op.
          account={{ id: line.sipAccountId, username: line.extension, agentId: line.extensionAgentId }}
          lineNumber={line.number}
          onClose={() => setMigrating(false)}
          onDone={() => { lineQ.refetch?.(); setMigrating(false); }}
        />
      )}

      {moving && (
        <MoveLineModal
          line={line}
          onClose={() => setMoving(false)}
          onDone={() => {
            // The page's own data is now about a different customer — refetch rather than leave the
            // old owner's name in the header over a line that is no longer theirs.
            lineQ.refetch?.();
            setMoving(false);
          }}
        />
      )}
      {confirmDelete && (
        <ConfirmDeleteDialog
          title={`Remove ${line.number || `line ${line.id}`}?`}
          body={line.provisioned
            // What "Take down" used to be, folded in: removing a live line has to take it
            // off the systems serving it first, or the extension and route outlive the
            // record that explains them.
            ? 'This line is live. It will be taken off the PBX, its phone and the carrier’s '
              + 'feature set first, and removed only if that succeeds. The number returns to '
              + 'inventory and stays yours; voicemail is kept.'
            : 'The line and its extension are removed and the number returns to inventory. '
              + 'Nothing is deleted at the carrier — the number stays yours.'}
          confirmLabel={line.provisioned ? 'Take down and remove' : 'Remove line'}
          isPending={del.isPending}
          error={del.error?.body?.error || del.error?.message}
          onCancel={() => setConfirmDelete(false)}
          onConfirm={() => {
            setConfirmDelete(false);
            if (line.provisioned) { setRemoveAfterTeardown(true); setRun('teardown'); }
            else del.mutate();
          }}
        />
      )}

      {settingsOpen && (
        <LineSettingsModal
          line={line}
          onClose={() => setSettingsOpen(false)}
          onSaved={() => {
            setSettingsOpen(false);
            // A live line whose 911 address moved is registered with the carrier for the
            // OLD one until a build re-runs set-e911-site, and nobody finds that out from a
            // 911 call. There WAS an amber notice here offering "Build it now…"; removed
            // 2026-08-15 (Kevin) along with the panel's button, because the same fact is
            // already listed as a pending change ("… is not registered with the carrier for
            // 911") and the action bar is now the single place to apply. `refresh()` is what
            // makes it appear there, so it is doing real work — not just a re-read.
            refresh();
          }}
        />
      )}

      {settingPort && (
        <SetDevicePortDialog
          line={line}
          extension={settingPort}
          subscriberId={line.subscriberId}
          onClose={() => setSettingPort(null)}
          onSaved={() => {
            setSettingPort(null);
            // The extensions list is its own query — refreshing the line alone leaves the row
            // showing no device, which is half of what this fixed.
            qc.invalidateQueries({ queryKey: ['line', String(id), 'extensions'] });
            refresh();
          }}
        />
      )}
    </div>
  );
}
